import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ME } from 'graphql/query/user-gql';
import { isUnauthenticatedError } from 'graphql/auth-error';
import { message } from 'antd';
import { Loader } from 'components/request-result';
import { localizeByKeyStatic } from 'components/service';
import { useNavigate } from 'react-router-dom';

interface PermissionsContextType {
  permissions: any[];
  isDeveloper: boolean;
  loading: boolean;
  hasPermission: (permissionSlug: string, entryPoint?: string, groupName?: string, action?: string) => boolean;
  hasAnyPermission: (permissionSlugs: string[]) => boolean;
  hasAllPermissions: (permissionSlugs: string[]) => boolean;
  showPermissionDenied: (permissionSlug: string) => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { data: meData, loading, error } = useQuery(GET_ME);
  const navigate = useNavigate();

  // Check if user has developer role
  const isDeveloper = meData?.me?.roles?.some((role: any) => role.slug === 'role_developer') || false;

  // Extract library permissions from user data
  const permissions = React.useMemo(() => {
    if (!meData?.me?.roles) return [];

    const allPermissions: any[] = [];

    // Extract permissions from all roles
    meData.me.roles.forEach((role: any) => {
      if (role.permissions) {
        role.permissions.forEach((perm: any) => {
          // Only include library permissions
          if (perm.entry_point === 'library' && perm.slug) {
            allPermissions.push(perm);
          }
        });
      }
    });

    // Remove duplicates
    return [...new Set(allPermissions)];
  }, [meData]);

  // Check if user has login permission
  const hasLoginPermission = React.useMemo(() => {
    if (isDeveloper) return true;
    return permissions.some(perm => perm.slug === 'permission_library_login');
  }, [isDeveloper, permissions]);

  // Redirect to login if no login permission
  React.useEffect(() => {
    if (!loading && meData?.me && !hasLoginPermission) {
      message.error(localizeByKeyStatic('PERMISSION.Error_NoAccessToSystem'));
      // Clear auth token and redirect to login
      localStorage.removeItem('authToken');
      navigate('/');
    }
  }, [loading, meData, hasLoginPermission, navigate]);

  // Check if user has specific permission
  const hasPermission = React.useCallback((permissionSlug: string, entryPoint?: string, groupName?: string, action?: string): boolean => {
    if (isDeveloper) return true;

    return permissions.some(perm => {
      // Check slug (required)
      if (perm.slug !== permissionSlug) return false;

      // Check entry_point (if passed)
      if (entryPoint && perm.entry_point !== entryPoint) return false;

      // Check group_name (if passed)
      if (groupName && perm.group_name !== groupName) return false;

      // Check action (if passed)
      if (action && perm.action !== action) return false;

      return true;
    });
  }, [isDeveloper, permissions]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = React.useCallback((permissionSlugs: string[]): boolean => {
    if (isDeveloper) return true;
    return permissionSlugs.some(slug => permissions.some(perm => perm.slug === slug));
  }, [isDeveloper, permissions]);

  // Check if user has all of the specified permissions
  const hasAllPermissions = React.useCallback((permissionSlugs: string[]): boolean => {
    if (isDeveloper) return true;
    return permissionSlugs.every(slug => permissions.includes(slug));
  }, [isDeveloper, permissions]);

  // Show permission denied message
  const showPermissionDenied = React.useCallback((permissionSlug: string) => {
    message.error(`${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(permissionSlug)}`);
  }, []);

  // Show error if query failed
  React.useEffect(() => {
    if (error && !isUnauthenticatedError(error)) {
      message.error(localizeByKeyStatic('PERMISSION.Error_FailedToLoad'));
    }
  }, [error]);

  const contextValue: PermissionsContextType = {
    permissions,
    isDeveloper,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    showPermissionDenied,
  };

  if (loading) {
    return <Loader />;
  }

  // Don't render children if user doesn't have login permission
  if (!hasLoginPermission) {
    return <Loader />;
  }

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }
  return context;
};
